import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Headset, UsersFour, Trophy, GearSix, Handshake, Megaphone, ChartLineUp,
  GraduationCap, PenNib, CaretDown, MapPin, Briefcase, ArrowRight, RocketLaunch,
  HeartStraight, TrendUp, Lightbulb, UsersThree,
} from "@phosphor-icons/react";
import { useSeo } from "../lib/useSeo";
import { COMPANY } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { Reveal, Stagger, StaggerItem, SectionBadge } from "../components/blocks";

const JobCard = ({ job, index, ui }) => {
  const [open, setOpen] = useState(false);
  const mailto = `mailto:${COMPANY.careersEmail}?subject=${encodeURIComponent(`Application for ${job.title} — Priyajit Systems`)}`;

  return (
    <div
      className={`overflow-hidden rounded-[1.5rem] border bg-white transition-all duration-300 ${
        open ? "border-electric/40 shadow-[0_16px_48px_rgba(30,127,240,0.12)]" : "border-navy/[0.07] shadow-[0_4px_20px_rgba(11,30,77,0.05)] hover:border-electric/25"
      }`}
      data-testid={`job-card-${index}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-4 px-6 py-5 text-left sm:px-8"
        data-testid={`job-toggle-${index}`}
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-electric/10 text-electric">
          <job.icon size={24} weight="duotone" />
        </span>
        <span className="flex-1">
          <span className="block font-display text-lg font-bold text-navy">{job.title}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-slate-500">
            <span className="flex items-center gap-1"><Briefcase size={13} /> {job.dept} · {ui.fullTime}</span>
            <span className="flex items-center gap-1"><MapPin size={13} /> NOIDA / {ui.remote}</span>
          </span>
        </span>
        <CaretDown size={18} className={`shrink-0 text-slate-400 transition-transform duration-300 ${open ? "rotate-180 text-electric" : ""}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="border-t border-navy/[0.06] px-6 pb-7 pt-6 sm:px-8">
              <p className="max-w-3xl text-[15px] leading-relaxed text-slate-600">{job.summary}</p>
              <div className="mt-6 grid gap-8 md:grid-cols-2">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-electric">{ui.respLabel}</h4>
                  <ul className="mt-3 space-y-2.5">
                    {job.responsibilities.map((r, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-electric" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-teal">{ui.reqLabel}</h4>
                  <ul className="mt-3 space-y-2.5">
                    {job.requirements.map((r, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-teal" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <a
                href={mailto}
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(30,127,240,0.35)] transition-all hover:-translate-y-0.5"
                data-testid={`job-apply-${index}`}
              >
                {ui.apply} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
              <p className="mt-3 text-xs text-slate-500">
                {ui.mailNote1} <a href={mailto} className="font-semibold text-electric hover:underline">{COMPANY.careersEmail}</a> {ui.mailNote2}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Careers() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "hi" ? hi : en);

  useSeo({
    title: "Careers | Join Our Team — Priyajit Systems",
    description: "Join Priyajit Systems and help build intelligent products for healthcare and careers. Open roles in support, sales, marketing, HR, operations, counselling and more. NOIDA / Remote.",
    image: "/og-image.jpg",
  });

  const ui = {
    fullTime: t("Full-time", "फुल-टाइम"),
    remote: t("Remote", "रिमोट"),
    respLabel: t("What You'll Do", "आप क्या करेंगे"),
    reqLabel: t("What We're Looking For", "हम क्या चाहते हैं"),
    apply: t("Apply via Email", "ईमेल से आवेदन करें"),
    mailNote1: t("Send your resume to", "अपना रिज़्यूमे भेजें"),
    mailNote2: t("with the role in the subject line.", "पर — सब्जेक्ट में पद का नाम लिखें।"),
  };

  const perks = [
    { icon: RocketLaunch, title: t("Ground-Floor Impact", "शुरुआत से असर"), desc: t("Join early and shape products used by hospitals and individuals across India.", "शुरुआत में जुड़ें और भारत भर के अस्पतालों व लोगों के काम आने वाले प्रोडक्ट गढ़ें।") },
    { icon: TrendUp, title: t("Fast Growth", "तेज़ ग्रोथ"), desc: t("Wide ownership, steep learning curves and real responsibility from day one.", "पहले दिन से पूरी ज़िम्मेदारी, तेज़ सीख और असली ओनरशिप।") },
    { icon: HeartStraight, title: t("Purpose Over Profit", "मुनाफ़े से पहले मक़सद"), desc: t("Work on technology that improves careers, wellbeing and healthcare.", "ऐसी टेक्नोलॉजी पर काम करें जो करियर, वेलबीइंग और हेल्थकेयर बेहतर बनाती है।") },
    { icon: UsersThree, title: t("People-First Culture", "पीपल-फ़र्स्ट कल्चर"), desc: t("Small, collaborative team led by founders who build alongside you.", "छोटी, मिलकर काम करने वाली टीम — फ़ाउंडर्स आपके साथ खड़े होकर बनाते हैं।") },
  ];

  const jobs = [
    {
      icon: Headset,
      title: "Customer Support Executive",
      dept: t("Support", "सपोर्ट"),
      summary: t("Be the friendly first point of contact for hospitals and users of our products, resolving queries quickly and empathetically.", "अस्पतालों और यूज़र्स के लिए पहला भरोसेमंद संपर्क बनें — सवालों का तेज़ और संवेदनशील समाधान करें।"),
      responsibilities: [
        t("Respond to customer queries via phone, email and chat within defined SLAs", "फ़ोन, ईमेल और चैट पर तय समय-सीमा में ग्राहकों के सवालों का जवाब देना"),
        t("Troubleshoot product issues, log tickets and escalate to the tech team when needed", "प्रोडक्ट समस्याओं का समाधान, टिकट लॉगिंग और ज़रूरत पर टेक टीम तक एस्केलेशन"),
        t("Guide new hospital staff through onboarding and day-to-day usage of Atikxzio HMS", "नए अस्पताल स्टाफ़ को Atikxzio HMS की ऑनबोर्डिंग और रोज़मर्रा के उपयोग में मदद"),
        t("Maintain help documentation and FAQs based on recurring queries", "बार-बार आने वाले सवालों के आधार पर हेल्प डॉक्यूमेंटेशन और FAQ बनाए रखना"),
        t("Share customer feedback with product teams to improve the platform", "प्लेटफ़ॉर्म बेहतर बनाने के लिए ग्राहकों का फ़ीडबैक प्रोडक्ट टीम तक पहुँचाना"),
      ],
      requirements: [
        t("0–2 years of experience in customer support or a client-facing role", "कस्टमर सपोर्ट या क्लाइंट-फ़ेसिंग भूमिका में 0–2 वर्ष का अनुभव"),
        t("Excellent verbal and written communication in English and Hindi", "अंग्रेज़ी और हिंदी में बेहतरीन मौखिक व लिखित संवाद"),
        t("Patience, empathy and a genuine problem-solving attitude", "धैर्य, संवेदनशीलता और समस्या सुलझाने का जुनून"),
        t("Comfort with SaaS tools, ticketing systems and basic troubleshooting", "SaaS टूल्स, टिकटिंग सिस्टम और बेसिक ट्रबलशूटिंग में सहजता"),
        t("Bonus: exposure to healthcare or hospital environments", "बोनस: हेल्थकेयर या अस्पताल के माहौल का अनुभव"),
      ],
    },
    {
      icon: UsersFour,
      title: "HR Executive",
      dept: t("People & Culture", "पीपल व कल्चर"),
      summary: t("Build and nurture the team that builds Priyajit Systems — own hiring, onboarding and a people-first culture.", "वह टीम बनाइए जो प्रियाजित सिस्टम्स बनाती है — हायरिंग, ऑनबोर्डिंग और पीपल-फ़र्स्ट कल्चर की ज़िम्मेदारी लें।"),
      responsibilities: [
        t("Manage end-to-end recruitment: sourcing, screening, interviews and offers", "एंड-टू-एंड रिक्रूटमेंट: सोर्सिंग, स्क्रीनिंग, इंटरव्यू और ऑफ़र"),
        t("Run smooth onboarding and induction for every new team member", "हर नए सदस्य की सहज ऑनबोर्डिंग और इंडक्शन"),
        t("Maintain HR records, payroll inputs, leave and attendance processes", "HR रिकॉर्ड, पेरोल इनपुट, छुट्टी और उपस्थिति प्रक्रियाएँ सँभालना"),
        t("Drive engagement initiatives, feedback cycles and grievance handling", "एंगेजमेंट पहल, फ़ीडबैक साइकिल और शिकायत निवारण"),
        t("Support policy creation and statutory compliance as we scale", "कंपनी बढ़ने के साथ पॉलिसी निर्माण और वैधानिक अनुपालन में सहयोग"),
      ],
      requirements: [
        t("1–3 years of HR experience, preferably in a startup or tech company", "HR में 1–3 वर्ष का अनुभव, स्टार्टअप/टेक कंपनी में बेहतर"),
        t("Working knowledge of recruitment platforms and HRMS tools", "रिक्रूटमेंट प्लेटफ़ॉर्म और HRMS टूल्स की जानकारी"),
        t("Strong interpersonal skills with high discretion and integrity", "मज़बूत इंटरपर्सनल स्किल्स, गोपनीयता और ईमानदारी"),
        t("Ability to multitask across hiring, operations and culture", "हायरिंग, ऑपरेशंस और कल्चर में मल्टीटास्किंग की क्षमता"),
        t("MBA/PGDM in HR or an equivalent qualification preferred", "HR में MBA/PGDM या समकक्ष योग्यता बेहतर"),
      ],
    },
    {
      icon: Trophy,
      title: "Customer Success Lead",
      dept: t("Customer Success", "कस्टमर सक्सेस"),
      summary: t("Own the post-sale journey for our hospital partners — from onboarding to renewal — and make every customer a success story.", "अस्पताल पार्टनर्स की बिक्री-बाद की पूरी यात्रा सँभालें — ऑनबोर्डिंग से रिन्यूअल तक — और हर ग्राहक को सक्सेस स्टोरी बनाएँ।"),
      responsibilities: [
        t("Lead onboarding, training and adoption for hospitals on Atikxzio HMS", "Atikxzio HMS पर अस्पतालों की ऑनबोर्डिंग, ट्रेनिंग और अपनाने की अगुवाई"),
        t("Build success plans, track health scores and drive renewals & upsells", "सक्सेस प्लान, हेल्थ स्कोर ट्रैकिंग और रिन्यूअल व अपसेल"),
        t("Act as the voice of the customer, channeling feedback into the roadmap", "ग्राहक की आवाज़ बनें — फ़ीडबैक को रोडमैप तक पहुँचाएँ"),
        t("Create playbooks, SOPs and training material for the success function", "सक्सेस फ़ंक्शन के लिए प्लेबुक, SOP और ट्रेनिंग सामग्री"),
        t("Hire and mentor the customer success team as we grow", "टीम बढ़ने पर कस्टमर सक्सेस टीम की हायरिंग और मेंटरिंग"),
      ],
      requirements: [
        t("3+ years in customer success, account management or implementation roles", "कस्टमर सक्सेस, अकाउंट मैनेजमेंट या इम्प्लीमेंटेशन में 3+ वर्ष"),
        t("Proven record of retention and account growth in B2B SaaS", "B2B SaaS में रिटेंशन और अकाउंट ग्रोथ का सिद्ध रिकॉर्ड"),
        t("Strong presentation, training and stakeholder-management skills", "प्रेज़ेंटेशन, ट्रेनिंग और स्टेकहोल्डर मैनेजमेंट में दक्षता"),
        t("Data-driven approach to adoption tracking and churn prevention", "अडॉप्शन ट्रैकिंग और चर्न रोकथाम में डेटा-आधारित तरीक़ा"),
        t("Bonus: experience with healthcare IT or HMS/EMR products", "बोनस: हेल्थकेयर IT या HMS/EMR प्रोडक्ट्स का अनुभव"),
      ],
    },
    {
      icon: GearSix,
      title: "Operations Lead",
      dept: t("Operations", "ऑपरेशंस"),
      summary: t("Keep the engine running — own day-to-day business operations, vendor management and cross-team execution.", "इंजन चालू रखिए — रोज़ के बिज़नेस ऑपरेशंस, वेंडर मैनेजमेंट और क्रॉस-टीम एग्ज़िक्यूशन की ज़िम्मेदारी लें।"),
      responsibilities: [
        t("Design and streamline internal processes across sales, support and delivery", "सेल्स, सपोर्ट और डिलीवरी की आंतरिक प्रक्रियाएँ बनाना और सरल करना"),
        t("Manage vendor relationships, procurement and office administration", "वेंडर संबंध, ख़रीद और ऑफ़िस प्रशासन"),
        t("Track budgets, invoices and operational metrics with monthly reporting", "बजट, इनवॉइस और ऑपरेशनल मेट्रिक्स की मासिक रिपोर्टिंग"),
        t("Coordinate hospital deployment logistics including on-site setup & training", "ऑन-साइट सेटअप व ट्रेनिंग सहित अस्पताल डिप्लॉयमेंट का समन्वय"),
        t("Ensure legal, compliance and documentation hygiene across the company", "कंपनी भर में लीगल, कंप्लायंस और डॉक्यूमेंटेशन की सफ़ाई"),
      ],
      requirements: [
        t("3+ years in operations, program management or a chief-of-staff style role", "ऑपरेशंस/प्रोग्राम मैनेजमेंट में 3+ वर्ष का अनुभव"),
        t("Strong organisational skills with obsessive attention to detail", "मज़बूत संगठन क्षमता और बारीकी पर पकड़"),
        t("Comfort with spreadsheets, dashboards and process documentation", "स्प्रेडशीट, डैशबोर्ड और प्रोसेस डॉक्यूमेंटेशन में सहजता"),
        t("Ability to work with ambiguity and wear multiple hats in a startup", "स्टार्टअप में अनिश्चितता और कई भूमिकाओं में काम करने की क्षमता"),
        t("MBA or operations certification is a plus", "MBA या ऑपरेशंस सर्टिफ़िकेशन बोनस"),
      ],
    },
    {
      icon: Handshake,
      title: "Business Development Executive",
      dept: t("Growth", "ग्रोथ"),
      summary: t("Open doors for Atikxzio HMS — identify hospitals, build relationships and convert conversations into partnerships.", "Atikxzio HMS के लिए दरवाज़े खोलिए — अस्पताल खोजें, रिश्ते बनाएँ और बातचीत को साझेदारी में बदलें।"),
      responsibilities: [
        t("Generate and qualify leads among hospitals, clinics and nursing homes", "अस्पतालों, क्लीनिकों और नर्सिंग होम में लीड जनरेट और क्वालिफ़ाई करना"),
        t("Conduct field visits, product demos and pitch meetings with decision-makers", "फ़ील्ड विज़िट, प्रोडक्ट डेमो और निर्णयकर्ताओं से मीटिंग"),
        t("Manage the sales pipeline in CRM with disciplined follow-ups", "CRM में पाइपलाइन मैनेजमेंट और अनुशासित फ़ॉलो-अप"),
        t("Negotiate proposals and close deals in line with pricing plans", "प्राइसिंग प्लान के अनुसार प्रस्तावों पर बातचीत और डील क्लोज़र"),
        t("Represent Priyajit Systems at healthcare events and conferences", "हेल्थकेयर इवेंट्स और कॉन्फ़्रेंस में कंपनी का प्रतिनिधित्व"),
      ],
      requirements: [
        t("1–3 years of B2B sales or business development experience", "B2B सेल्स या बिज़नेस डेवलपमेंट में 1–3 वर्ष"),
        t("Confident communicator with strong negotiation skills", "आत्मविश्वासी संवाद और मज़बूत नेगोशिएशन"),
        t("Willingness to travel for hospital visits and demos", "अस्पताल विज़िट व डेमो के लिए यात्रा की तत्परता"),
        t("Self-driven, target-oriented and resilient", "स्व-प्रेरित, टार्गेट-केंद्रित और जुझारू"),
        t("Bonus: existing network in the healthcare/hospital ecosystem", "बोनस: हेल्थकेयर/अस्पताल इकोसिस्टम में नेटवर्क"),
      ],
    },
    {
      icon: Megaphone,
      title: "Marketing Executive",
      dept: t("Marketing", "मार्केटिंग"),
      summary: t("Tell our story — run campaigns that build the Priyajit Systems and Atikxzio brands across digital channels.", "हमारी कहानी सुनाइए — डिजिटल चैनलों पर Priyajit Systems और Atikxzio ब्रांड बनाने वाले कैंपेन चलाएँ।"),
      responsibilities: [
        t("Plan and execute digital campaigns across social, search and email", "सोशल, सर्च और ईमेल पर डिजिटल कैंपेन की योजना और क्रियान्वयन"),
        t("Manage social media calendars, creatives and community engagement", "सोशल मीडिया कैलेंडर, क्रिएटिव और कम्युनिटी एंगेजमेंट"),
        t("Track campaign performance and optimise for leads and awareness", "कैंपेन परफ़ॉर्मेंस ट्रैकिंग और लीड/अवेयरनेस के लिए ऑप्टिमाइज़ेशन"),
        t("Coordinate with designers and writers for brand-consistent content", "ब्रांड-अनुरूप कंटेंट के लिए डिज़ाइनर व राइटर से समन्वय"),
        t("Support product launches, webinars and event marketing", "प्रोडक्ट लॉन्च, वेबिनार और इवेंट मार्केटिंग में सहयोग"),
      ],
      requirements: [
        t("1–3 years of digital marketing experience, preferably B2B or SaaS", "डिजिटल मार्केटिंग में 1–3 वर्ष, B2B/SaaS बेहतर"),
        t("Hands-on with Meta/Google Ads, SEO basics and email tools", "Meta/Google Ads, SEO और ईमेल टूल्स का व्यावहारिक अनुभव"),
        t("Strong copy sense and eye for clean, on-brand creatives", "कॉपी की समझ और साफ़, ऑन-ब्रांड क्रिएटिव की नज़र"),
        t("Analytical mindset with comfort in GA4 and campaign dashboards", "GA4 व कैंपेन डैशबोर्ड में सहज विश्लेषणात्मक सोच"),
        t("Degree in marketing/communications preferred", "मार्केटिंग/कम्युनिकेशन में डिग्री बेहतर"),
      ],
    },
    {
      icon: ChartLineUp,
      title: "Sales Executive",
      dept: t("Sales", "सेल्स"),
      summary: t("Convert interest into revenue — run demos, handle objections and close subscriptions for Atikxzio HMS.", "रुचि को आमदनी में बदलिए — डेमो चलाएँ, आपत्तियाँ सँभालें और Atikxzio HMS की सब्सक्रिप्शन क्लोज़ करें।"),
      responsibilities: [
        t("Own inbound and outbound sales conversations end to end", "इनबाउंड व आउटबाउंड सेल्स बातचीत की पूरी ज़िम्मेदारी"),
        t("Deliver compelling product demos tailored to each hospital's workflow", "हर अस्पताल के वर्कफ़्लो के अनुसार असरदार प्रोडक्ट डेमो"),
        t("Prepare quotations, negotiate and close within pricing guidelines", "कोटेशन, नेगोशिएशन और प्राइसिंग नियमों में क्लोज़िंग"),
        t("Maintain accurate CRM records and forecast monthly numbers", "सटीक CRM रिकॉर्ड और मासिक फ़ोरकास्ट"),
        t("Collaborate with customer success for smooth handovers post-sale", "बिक्री के बाद कस्टमर सक्सेस को सहज हैंडओवर"),
      ],
      requirements: [
        t("1–3 years of quota-carrying sales experience, SaaS preferred", "टार्गेट-आधारित सेल्स में 1–3 वर्ष, SaaS बेहतर"),
        t("Excellent persuasion, listening and objection-handling skills", "बेहतरीन प्रेरक क्षमता, सुनने और आपत्ति सँभालने का हुनर"),
        t("Fluent in English and Hindi; regional languages are a plus", "अंग्रेज़ी व हिंदी में प्रवाह; क्षेत्रीय भाषाएँ बोनस"),
        t("Discipline with pipelines, follow-ups and reporting", "पाइपलाइन, फ़ॉलो-अप और रिपोर्टिंग में अनुशासन"),
        t("Hunger to grow with an early-stage company", "अर्ली-स्टेज कंपनी के साथ बढ़ने की भूख"),
      ],
    },
    {
      icon: GraduationCap,
      title: "Career & Academic Counsellor",
      dept: "Atikxzio CMH",
      summary: t("Guide students and professionals to the right career decisions on our Atikxzio CMH platform, blending empathy with expertise.", "Atikxzio CMH प्लेटफ़ॉर्म पर छात्रों और प्रोफेशनल्स को सही करियर निर्णय की ओर ले जाइए — संवेदनशीलता और विशेषज्ञता के साथ।"),
      responsibilities: [
        t("Conduct one-on-one career counselling sessions for students & professionals", "छात्रों व प्रोफेशनल्स के लिए वन-टू-वन करियर काउंसलिंग सेशन"),
        t("Interpret psychometric assessments and translate them into clear guidance", "साइकोमेट्रिक असेसमेंट की व्याख्या और स्पष्ट मार्गदर्शन"),
        t("Create personalised career roadmaps covering courses, exams and skills", "कोर्स, परीक्षा और स्किल्स सहित व्यक्तिगत करियर रोडमैप"),
        t("Contribute to counselling frameworks and content on the CMH platform", "CMH प्लेटफ़ॉर्म पर काउंसलिंग फ़्रेमवर्क और कंटेंट में योगदान"),
        t("Maintain session records and follow up on counselee progress", "सेशन रिकॉर्ड और काउंसली की प्रगति पर फ़ॉलो-अप"),
      ],
      requirements: [
        t("Degree in psychology, education or counselling (PG preferred)", "साइकोलॉजी/शिक्षा/काउंसलिंग में डिग्री (PG बेहतर)"),
        t("1–3 years of career/academic counselling experience", "करियर/एकेडमिक काउंसलिंग में 1–3 वर्ष"),
        t("Familiarity with psychometric tools and Indian education pathways", "साइकोमेट्रिक टूल्स और भारतीय शिक्षा मार्गों की समझ"),
        t("Warm, empathetic communication with strong listening skills", "गर्मजोशी भरा, संवेदनशील संवाद और सुनने की क्षमता"),
        t("Certification in career counselling (CCC/GCC etc.) is a plus", "करियर काउंसलिंग सर्टिफ़िकेशन (CCC/GCC आदि) बोनस"),
      ],
    },
    {
      icon: PenNib,
      title: "Content Writer",
      dept: t("Marketing", "मार्केटिंग"),
      summary: t("Write words that build trust — blogs, website copy, product content and campaigns for both our brands.", "भरोसा बनाने वाले शब्द लिखिए — दोनों ब्रांड्स के लिए ब्लॉग, वेबसाइट कॉपी, प्रोडक्ट कंटेंट और कैंपेन।"),
      responsibilities: [
        t("Write blogs, landing pages, case studies and email campaigns", "ब्लॉग, लैंडिंग पेज, केस स्टडी और ईमेल कैंपेन लिखना"),
        t("Create product content: feature explainers, help articles and FAQs", "प्रोडक्ट कंटेंट: फ़ीचर एक्सप्लेनर, हेल्प आर्टिकल और FAQ"),
        t("Adapt tone across B2B (hospitals) and B2C (students & professionals)", "B2B (अस्पताल) और B2C (छात्र/प्रोफेशनल) के अनुसार टोन बदलना"),
        t("Research healthcare and career-tech topics for authoritative content", "प्रभावशाली कंटेंट के लिए हेल्थकेयर व करियर-टेक विषयों पर रिसर्च"),
        t("Optimise all content for SEO and readability", "सारा कंटेंट SEO और पठनीयता के लिए ऑप्टिमाइज़ करना"),
      ],
      requirements: [
        t("1–3 years of content writing experience, portfolio required", "कंटेंट राइटिंग में 1–3 वर्ष, पोर्टफ़ोलियो ज़रूरी"),
        t("Impeccable English grammar with a clear, crisp style", "बेदाग़ अंग्रेज़ी व्याकरण और साफ़, सधी शैली"),
        t("Working knowledge of SEO, keywords and content tools", "SEO, कीवर्ड और कंटेंट टूल्स की जानकारी"),
        t("Ability to simplify technical/healthcare topics for lay readers", "तकनीकी/हेल्थकेयर विषयों को आसान भाषा में समझाने की क्षमता"),
        t("Bonus: Hindi writing skills for regional campaigns", "बोनस: क्षेत्रीय कैंपेन के लिए हिंदी लेखन"),
      ],
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-white to-white pt-36 pb-16 lg:pb-20" data-testid="careers-hero">
        <div aria-hidden className="absolute inset-0 bg-grid-light" />
        <div aria-hidden className="absolute -right-32 top-10 h-96 w-96 rounded-full bg-electric/10 blur-[100px]" />
        <div aria-hidden className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-teal/10 blur-[100px]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-electric/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-electric">
              <Lightbulb size={14} weight="fill" /> {t("Careers at Priyajit Systems", "प्रियाजित सिस्टम्स में करियर")}
            </span>
            <h1 className="mt-7 font-display text-5xl font-black leading-[1.08] tracking-tighter text-navy sm:text-6xl">
              {t("Join our team. ", "हमारी टीम से जुड़िए। ")}<span className="text-gradient-electric">{t("Build what matters.", "वह बनाइए जो मायने रखता है।")}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              {t("We're a young team building intelligent products for healthcare and careers. If you want ownership, purpose and real impact — there's a seat for you.", "हम हेल्थकेयर और करियर के लिए इंटेलिजेंट प्रोडक्ट बनाने वाली एक युवा टीम हैं। ओनरशिप, मक़सद और असली असर चाहिए — तो आपकी सीट तैयार है।")}
            </p>
            <a
              href="#open-roles"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(30,127,240,0.4)] transition-all hover:-translate-y-0.5"
              data-testid="careers-view-roles"
            >
              {t("View Open Roles", "खुले पद देखें")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Perks */}
      <section className="border-y border-navy/[0.05] bg-white py-16" data-testid="careers-perks">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {perks.map((p, i) => (
              <StaggerItem key={i}>
                <div className="flex h-full flex-col rounded-2xl border border-navy/[0.06] bg-gradient-to-b from-slate-50 to-white p-6 transition-all hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgba(30,127,240,0.12)]" data-testid={`careers-perk-${i}`}>
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 text-teal"><p.icon size={24} weight="duotone" /></div>
                  <h3 className="font-display text-base font-bold text-navy">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Openings */}
      <section id="open-roles" className="bg-[#F8FAFC] py-24 lg:py-32" data-testid="careers-openings">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <Reveal><SectionBadge number="01" label={t("Open Roles", "खुले पद")} /></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-black tracking-tight text-navy sm:text-5xl">
              {t("Current ", "मौजूदा ")}<span className="text-gradient-electric">{t("openings.", "ओपनिंग्स।")}</span>
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t("All roles are full-time — work from our NOIDA office or from home (WFH). Click a role to see the full description.", "सभी पद फुल-टाइम हैं — हमारे NOIDA ऑफ़िस से या घर से (WFH) काम करें। पूरा विवरण देखने के लिए पद पर क्लिक करें।")}</p>
          </Reveal>
          <div className="mt-12 space-y-4">
            {jobs.map((job, i) => (
              <Reveal key={i} delay={Math.min(i * 0.05, 0.3)}>
                <JobCard job={job} index={i} ui={ui} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 lg:py-24" data-testid="careers-cta">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-navy grain px-8 py-14 text-center sm:px-16">
            <div aria-hidden className="absolute inset-0 bg-dots opacity-30" />
            <div className="relative">
              <h2 className="font-display text-3xl font-black tracking-tight text-white sm:text-4xl">{t("Don't see your role?", "आपका पद यहाँ नहीं दिखा?")}</h2>
              <p className="mx-auto mt-4 max-w-xl text-slate-300">{t("We're always looking for exceptional people. Tell us how you can add value.", "हम हमेशा बेहतरीन लोगों की तलाश में रहते हैं। बताइए आप क्या ख़ास ला सकते हैं।")}</p>
              <a
                href={`mailto:${COMPANY.careersEmail}?subject=${encodeURIComponent("Open Application — Priyajit Systems")}`}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-navy transition-all hover:-translate-y-0.5"
                data-testid="careers-open-application"
              >
                {t("Write to", "लिखें")} {COMPANY.careersEmail} <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
